import axios from 'axios';
import { parseErrorMessage } from '@/utils/errorParser';
import { API_ENDPOINTS } from './types';

/**
 * Workspace-related types
 */

export interface WorkspaceDTO {
  id: number;
  workspaceId: string;
  tenantId: string;
  name: string;
  slug: string;
  description?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  isDefault: boolean;
  settings?: string;
  icon?: string;
  color?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  workflowCount?: number;
  memberCount?: number;
}

export interface WorkspaceCreateRequest {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  color?: string;
  settings?: string;
  isDefault?: boolean;
}

export interface WorkspaceUpdateRequest {
  name?: string;
  slug?: string;
  description?: string;
  icon?: string;
  color?: string;
  settings?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
}

export interface UserWorkspaceDTO {
  id: number;
  keycloakUserId: string;
  workspaceId: string;
  workspaceName: string;
  workspaceSlug: string;
  role: 'WORKSPACE_ADMIN' | 'EDITOR' | 'VIEWER';
  email?: string;
  displayName?: string;
  joinedAt?: string;
  addedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AddWorkspaceMemberRequest {
  email?: string;
  keycloakUserId?: string;
  role: 'WORKSPACE_ADMIN' | 'EDITOR' | 'VIEWER';
}

export interface UpdateWorkspaceMemberRoleRequest {
  role: 'WORKSPACE_ADMIN' | 'EDITOR' | 'VIEWER';
}

/**
 * Workspace API client
 */
const workspaceApi = {
  /**
   * Get all workspaces for a tenant
   */
  getWorkspaces: async (tenantId: string): Promise<WorkspaceDTO[]> => {
    try {
      const response = await axios.get<WorkspaceDTO[]>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces`
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Get a specific workspace by ID
   */
  getWorkspace: async (tenantId: string, workspaceId: string): Promise<WorkspaceDTO> => {
    try {
      const response = await axios.get<WorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}`
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Get workspace by slug
   */
  getWorkspaceBySlug: async (tenantId: string, slug: string): Promise<WorkspaceDTO> => {
    try {
      const response = await axios.get<WorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/by-slug/${slug}`
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Get the default workspace for a tenant
   */
  getDefaultWorkspace: async (tenantId: string): Promise<WorkspaceDTO> => {
    try {
      const response = await axios.get<WorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/default`
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Create a new workspace
   */
  createWorkspace: async (tenantId: string, request: WorkspaceCreateRequest): Promise<WorkspaceDTO> => {
    try {
      const response = await axios.post<WorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces`,
        request
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Update a workspace
   */
  updateWorkspace: async (
    tenantId: string,
    workspaceId: string,
    request: WorkspaceUpdateRequest
  ): Promise<WorkspaceDTO> => {
    try {
      const response = await axios.put<WorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}`,
        request
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Archive a workspace (soft delete)
   */
  archiveWorkspace: async (tenantId: string, workspaceId: string): Promise<void> => {
    try {
      await axios.post(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}/archive`
      );
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Unarchive a workspace (restore from archive) by setting status back to ACTIVE
   */
  unarchiveWorkspace: async (tenantId: string, workspaceId: string): Promise<WorkspaceDTO> => {
    try {
      const response = await axios.put<WorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}`,
        { status: 'ACTIVE' }
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Delete a workspace permanently
   */
  deleteWorkspace: async (tenantId: string, workspaceId: string): Promise<void> => {
    try {
      await axios.delete(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}`
      );
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Set a workspace as the default
   */
  setDefaultWorkspace: async (tenantId: string, workspaceId: string): Promise<WorkspaceDTO> => {
    try {
      const response = await axios.post<WorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}/set-default`
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  // ==================== Workspace Members ====================

  /**
   * Get all members of a workspace
   */
  getWorkspaceMembers: async (tenantId: string, workspaceId: string): Promise<UserWorkspaceDTO[]> => {
    try {
      const response = await axios.get<UserWorkspaceDTO[]>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}/members`
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Add a member to a workspace
   */
  addWorkspaceMember: async (
    tenantId: string,
    workspaceId: string,
    request: AddWorkspaceMemberRequest
  ): Promise<UserWorkspaceDTO> => {
    try {
      const response = await axios.post<UserWorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}/members`,
        request
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Update a member's role in a workspace
   */
  updateWorkspaceMemberRole: async (
    tenantId: string,
    workspaceId: string,
    memberId: string,
    request: UpdateWorkspaceMemberRoleRequest
  ): Promise<UserWorkspaceDTO> => {
    try {
      const response = await axios.put<UserWorkspaceDTO>(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}/members/${memberId}`,
        request
      );
      return response.data;
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },

  /**
   * Remove a member from a workspace
   */
  removeWorkspaceMember: async (
    tenantId: string,
    workspaceId: string,
    memberId: string
  ): Promise<void> => {
    try {
      await axios.delete(
        `${API_ENDPOINTS.WORKFLOW_BASE}/api/v1/tenants/${tenantId}/workspaces/${workspaceId}/members/${memberId}`
      );
    } catch (error) {
      throw new Error(parseErrorMessage(error));
    }
  },
};

export default workspaceApi;
